import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ImagePlus, X } from "lucide-react";
import { DEFAULT_PILLARS, DEFAULT_WHYUS, DEFAULT_SERVICE_CARDS } from "@/lib/texts";
import { photoUrl } from "@/components/Portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-[#E75C15]";

const saveKey = async (key, value, setSaving, label) => {
    setSaving(true);
    try {
        await axios.put(`${API}/settings/${key}`, { value }, { withCredentials: true });
        toast.success(`${label} aggiornati: sono già visibili sul sito.`);
    } catch {
        toast.error("Salvataggio non riuscito.");
    } finally {
        setSaving(false);
    }
};

const ItemRow = ({ children, onRemove, testId }) => (
    <div className="relative border border-stone-900/10 bg-white p-5" data-testid={testId}>
        <button
            onClick={onRemove}
            data-testid={`${testId}-remove`}
            className="absolute right-3 top-3 p-1.5 text-stone-400 transition-colors hover:text-[#B8430D]"
            aria-label="Rimuovi elemento"
        >
            <Trash2 className="h-4 w-4" />
        </button>
        {children}
    </div>
);

export const HomeBlocksForm = ({ settings }) => {
    const [pillars, setPillars] = useState(DEFAULT_PILLARS);
    const [stats, setStats] = useState(DEFAULT_WHYUS.stats);
    const [creds, setCreds] = useState(DEFAULT_WHYUS.credentials);
    const [cards, setCards] = useState(DEFAULT_SERVICE_CARDS);
    const [savingP, setSavingP] = useState(false);
    const [savingW, setSavingW] = useState(false);
    const [savingC, setSavingC] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    const fileRefs = useRef({});

    useEffect(() => {
        if (Array.isArray(settings?.about_pillars) && settings.about_pillars.length)
            setPillars(settings.about_pillars);
        if (settings?.whyus) {
            if (Array.isArray(settings.whyus.stats) && settings.whyus.stats.length)
                setStats(settings.whyus.stats);
            if (Array.isArray(settings.whyus.credentials) && settings.whyus.credentials.length)
                setCreds(settings.whyus.credentials);
        }
        if (Array.isArray(settings?.services_cards) && settings.services_cards.length)
            setCards(settings.services_cards);
    }, [settings]);

    const edit = (setter) => (i, k, v) =>
        setter((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: v } : it)));

    const saveWhyus = () => {
        const cleanStats = stats
            .map((s) => ({ value: Number(s.value) || 0, suffix: s.suffix || "", label: (s.label || "").trim() }))
            .filter((s) => s.label);
        const cleanCreds = creds
            .map((c) => ({ title: (c.title || "").trim(), desc: (c.desc || "").trim() }))
            .filter((c) => c.title);
        if (!cleanStats.length) {
            toast.error("Serve almeno una statistica con etichetta.");
            return;
        }
        saveKey("whyus", { stats: cleanStats, credentials: cleanCreds }, setSavingW, "Sezione Perché Noi");
    };

    const savePillars = () => {
        const clean = pillars
            .map((p) => ({ title: (p.title || "").trim(), desc: (p.desc || "").trim() }))
            .filter((p) => p.title);
        if (!clean.length) {
            toast.error("Serve almeno un punto di forza con titolo.");
            return;
        }
        saveKey("about_pillars", clean, setSavingP, "Punti di forza");
    };

    const uploadCardPhoto = async (i, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingIdx(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
            edit(setCards)(i, "photo", res.data.url);
            toast.success("Foto caricata.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Caricamento non riuscito.");
        } finally {
            setUploadingIdx(null);
            if (fileRefs.current[i]) fileRefs.current[i].value = "";
        }
    };

    const saveCards = () => {
        const clean = cards
            .map((c) => ({ title: (c.title || "").trim(), desc: (c.desc || "").trim(), photo: c.photo || "" }))
            .filter((c) => c.title);
        if (!clean.length) {
            toast.error("Serve almeno una scheda con titolo.");
            return;
        }
        saveKey("services_cards", clean, setSavingC, "Schede servizi");
    };

    return (
        <div className="max-w-3xl space-y-16" data-testid="home-blocks-form">
            <div>
                <h2 className="font-display text-2xl font-light text-stone-900">
                    Servizi — Schede della sezione
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">
                    Le schede della griglia Servizi in home. Con una foto diventano schede
                    fotografiche grandi, senza foto restano schede chiare con icona. Le prime due
                    sono larghe, le altre si dispongono a tre per riga.
                </p>
                <div className="mt-6 space-y-4">
                    {cards.map((c, i) => (
                        <ItemRow
                            key={i}
                            testId={`card-${i}`}
                            onRemove={() => setCards((arr) => arr.filter((_, j) => j !== i))}
                        >
                            <div className="space-y-3 pr-8">
                                <input
                                    data-testid={`card-title-${i}`}
                                    className={inputCls}
                                    placeholder="Titolo"
                                    value={c.title}
                                    onChange={(e) => edit(setCards)(i, "title", e.target.value)}
                                />
                                <textarea
                                    data-testid={`card-desc-${i}`}
                                    rows={2}
                                    className={`${inputCls} resize-y`}
                                    placeholder="Descrizione"
                                    value={c.desc}
                                    onChange={(e) => edit(setCards)(i, "desc", e.target.value)}
                                />
                                <div className="flex items-center gap-4">
                                    {c.photo ? (
                                        <div className="relative h-16 w-24 overflow-hidden border border-stone-900/10">
                                            <img src={photoUrl(c.photo)} alt="" className="h-full w-full object-cover" />
                                            <button
                                                data-testid={`card-photo-remove-${i}`}
                                                onClick={() => edit(setCards)(i, "photo", "")}
                                                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100"
                                                aria-label="Rimuovi foto"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-stone-400">Nessuna foto (scheda con icona)</span>
                                    )}
                                    <button
                                        data-testid={`card-photo-upload-${i}`}
                                        onClick={() => fileRefs.current[i]?.click()}
                                        disabled={uploadingIdx === i}
                                        className="inline-flex items-center gap-2 border border-stone-900/20 px-4 py-2 text-xs text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900 disabled:opacity-50"
                                    >
                                        {uploadingIdx === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                                        {c.photo ? "Cambia foto" : "Aggiungi foto"}
                                    </button>
                                    <input
                                        ref={(el) => (fileRefs.current[i] = el)}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid={`card-photo-input-${i}`}
                                        onChange={(e) => uploadCardPhoto(i, e)}
                                    />
                                </div>
                            </div>
                        </ItemRow>
                    ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        data-testid="card-add"
                        onClick={() => setCards((arr) => [...arr, { title: "", desc: "", photo: "" }])}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Aggiungi Scheda
                    </button>
                    <button
                        data-testid="cards-save"
                        onClick={saveCards}
                        disabled={savingC}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors hover:bg-[#B8430D] disabled:opacity-60"
                    >
                        {savingC ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Salva Schede Servizi
                    </button>
                </div>
            </div>

            <div>
                <h2 className="font-display text-2xl font-light text-stone-900">
                    Chi Siamo — Punti di forza
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">
                    Le tre voci con icona sotto il testo della sezione Chi Siamo. Le icone restano
                    fisse, cambi titolo e descrizione.
                </p>
                <div className="mt-6 space-y-4">
                    {pillars.map((p, i) => (
                        <ItemRow
                            key={i}
                            testId={`pillar-${i}`}
                            onRemove={() => setPillars((arr) => arr.filter((_, j) => j !== i))}
                        >
                            <div className="space-y-3 pr-8">
                                <input
                                    data-testid={`pillar-title-${i}`}
                                    className={inputCls}
                                    placeholder="Titolo"
                                    value={p.title}
                                    onChange={(e) => edit(setPillars)(i, "title", e.target.value)}
                                />
                                <textarea
                                    data-testid={`pillar-desc-${i}`}
                                    rows={2}
                                    className={`${inputCls} resize-y`}
                                    placeholder="Descrizione"
                                    value={p.desc}
                                    onChange={(e) => edit(setPillars)(i, "desc", e.target.value)}
                                />
                            </div>
                        </ItemRow>
                    ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        data-testid="pillar-add"
                        onClick={() => setPillars((arr) => [...arr, { title: "", desc: "" }])}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Aggiungi
                    </button>
                    <button
                        data-testid="pillars-save"
                        onClick={savePillars}
                        disabled={savingP}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors hover:bg-[#B8430D] disabled:opacity-60"
                    >
                        {savingP ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Salva Punti di Forza
                    </button>
                </div>
            </div>

            <div>
                <h2 className="font-display text-2xl font-light text-stone-900">
                    Perché Noi — Statistiche e credenziali
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">
                    I numeri animati (valore, simbolo, etichetta) e le credenziali con icona a
                    sinistra della sezione.
                </p>

                <h3 className="mt-6 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-[#C24E0F]">
                    Statistiche
                </h3>
                <div className="mt-4 space-y-4">
                    {stats.map((s, i) => (
                        <ItemRow
                            key={i}
                            testId={`stat-${i}`}
                            onRemove={() => setStats((arr) => arr.filter((_, j) => j !== i))}
                        >
                            <div className="grid grid-cols-[90px_70px_1fr] gap-3 pr-8">
                                <input
                                    type="number"
                                    data-testid={`stat-value-${i}`}
                                    className={inputCls}
                                    placeholder="Valore"
                                    value={s.value}
                                    onChange={(e) => edit(setStats)(i, "value", e.target.value)}
                                />
                                <input
                                    data-testid={`stat-suffix-${i}`}
                                    className={inputCls}
                                    placeholder="+ / %"
                                    value={s.suffix}
                                    onChange={(e) => edit(setStats)(i, "suffix", e.target.value)}
                                />
                                <input
                                    data-testid={`stat-label-${i}`}
                                    className={inputCls}
                                    placeholder="Etichetta"
                                    value={s.label}
                                    onChange={(e) => edit(setStats)(i, "label", e.target.value)}
                                />
                            </div>
                        </ItemRow>
                    ))}
                </div>
                <button
                    data-testid="stat-add"
                    onClick={() => setStats((arr) => [...arr, { value: 0, suffix: "+", label: "" }])}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900"
                >
                    <Plus className="h-3.5 w-3.5" />
                    Aggiungi Statistica
                </button>

                <h3 className="mt-8 font-mono-alt text-[11px] uppercase tracking-[0.25em] text-[#C24E0F]">
                    Credenziali
                </h3>
                <div className="mt-4 space-y-4">
                    {creds.map((c, i) => (
                        <ItemRow
                            key={i}
                            testId={`cred-${i}`}
                            onRemove={() => setCreds((arr) => arr.filter((_, j) => j !== i))}
                        >
                            <div className="space-y-3 pr-8">
                                <input
                                    data-testid={`cred-title-${i}`}
                                    className={inputCls}
                                    placeholder="Titolo"
                                    value={c.title}
                                    onChange={(e) => edit(setCreds)(i, "title", e.target.value)}
                                />
                                <textarea
                                    data-testid={`cred-desc-${i}`}
                                    rows={2}
                                    className={`${inputCls} resize-y`}
                                    placeholder="Descrizione"
                                    value={c.desc}
                                    onChange={(e) => edit(setCreds)(i, "desc", e.target.value)}
                                />
                            </div>
                        </ItemRow>
                    ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        data-testid="cred-add"
                        onClick={() => setCreds((arr) => [...arr, { title: "", desc: "" }])}
                        className="inline-flex items-center gap-2 rounded-full border border-stone-900/20 px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-600 transition-colors hover:border-[#E75C15] hover:text-stone-900"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Aggiungi Credenziale
                    </button>
                    <button
                        data-testid="whyus-save"
                        onClick={saveWhyus}
                        disabled={savingW}
                        className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors hover:bg-[#B8430D] disabled:opacity-60"
                    >
                        {savingW ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Salva Perché Noi
                    </button>
                </div>
            </div>
        </div>
    );
};
